/**
 * LexDuo · F1 Ejecuciones · Listado principal
 * Reemplaza el contenido del tab "Ejecuciones" en Tareas y se monta también
 * como página standalone en `/ejecuciones`.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Gavel, Plus, Search, Loader2, AlertTriangle, Banknote, Hammer, Filter,
  FileText, ArrowRight, RefreshCw,
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import EjecucionForm from '../components/ejecuciones/EjecucionForm';
import { obtenerToken } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ejecuciones`;

const ESTADOS_LABEL = {
  borrador:             { label: 'Borrador',             color: 'bg-gray-200 text-gray-700' },
  validacion_pendiente: { label: 'Validación pendiente', color: 'bg-amber-100 text-amber-800' },
  validacion_bloqueada: { label: 'Bloqueada',            color: 'bg-red-100 text-red-700' },
  apta_demanda:         { label: 'Apta para demanda',    color: 'bg-blue-100 text-blue-800' },
  demanda_presentada:   { label: 'Demanda presentada',   color: 'bg-indigo-100 text-indigo-800' },
  en_tramitacion:       { label: 'En tramitaciónnn',       color: 'bg-violet-100 text-violet-800' },
  suspendida:           { label: 'Suspendida',           color: 'bg-orange-100 text-orange-800' },
  satisfecha:           { label: 'Satisfecha',           color: 'bg-emerald-100 text-emerald-800' },
  archivada:            { label: 'Archivada',            color: 'bg-stone-200 text-stone-600' },
};

const TIPO_LABEL = {
  dineraria: { label: 'Dineraria', icon: Banknote, color: 'text-emerald-700' },
  hacer:     { label: 'De hacer',  icon: Hammer,   color: 'text-amber-700' },
  mixta:     { label: 'Mixta',     icon: Gavel,    color: 'text-violet-700' },
};

const headers = () => {
  const tok = obtenerToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const Ejecuciones = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [formOpen, setFormOpen] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (filtroEstado !== 'todos') params.estado = filtroEstado;
      if (filtroTipo !== 'todos') params.tipo = filtroTipo;
      if (busqueda.trim()) params.busqueda = busqueda.trim();

      const [r1, r2] = await Promise.all([
        axios.get(API, { params, headers: headers() }),
        axios.get(`${API}/stats`, { headers: headers() }),
      ]);
      setItems(r1.data?.items || []);
      setTotal(r1.data?.total || 0);
      setStats(r2.data);
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [page, filtroEstado, filtroTipo]);

  const onBuscar = () => { setPage(1); cargar(); };

  const onNueva = () => setFormOpen(true);

  const onCreated = (eje) => {
    setFormOpen(false);
    toast({ title: 'Ejecución creada', description: `Registro ${eje.numero_registro}` });
    navigate(`/ejecuciones/${eje.id}`);
  };

  const renderEstado = (estado) => {
    const c = ESTADOS_LABEL[estado] || { label: estado, color: 'bg-gray-100' };
    return <Badge className={`${c.color} hover:${c.color}`} data-testid={`badge-estado-${estado}`}>{c.label}</Badge>;
  };

  const renderTipo = (tipo) => {
    const t = TIPO_LABEL[tipo] || { label: tipo, icon: Gavel, color: 'text-gray-700' };
    const Icon = t.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 text-sm ${t.color}`}>
        <Icon className="w-3.5 h-3.5" /> {t.label}
      </span>
    );
  };

  return (
    <div className={embedded ? "space-y-4" : "p-6 space-y-6"} data-testid="ejecuciones-page">
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Gavel className="w-7 h-7 text-violet-700" /> Ejecuciones
            </h1>
            <p className="text-sm text-gray-500 mt-1">Tramitación de ejecuciones judiciales de sentencias</p>
          </div>
          <Button onClick={onNueva} className="bg-violet-700 hover:bg-violet-800" data-testid="btn-nueva-ejecucion">
            <Plus className="w-4 h-4 mr-2" /> Nueva ejecución
          </Button>
        </div>
      )}

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card data-testid="stat-total">
            <CardContent className="pt-4">
              <div className="text-xs text-gray-500">Total</div>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card className="border-amber-200" data-testid="stat-pendientes">
            <CardContent className="pt-4">
              <div className="text-xs text-amber-700 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Pendientes validar
              </div>
              <div className="text-2xl font-bold text-amber-700">{stats.pendientes_validar}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-tramitacion">
            <CardContent className="pt-4">
              <div className="text-xs text-violet-700">En tramitaciónnn</div>
              <div className="text-2xl font-bold text-violet-700">{stats.por_estado?.en_tramitacion || 0}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-dineraria">
            <CardContent className="pt-4">
              <div className="text-xs text-emerald-700 flex items-center gap-1">
                <Banknote className="w-3 h-3" /> Dineraria
              </div>
              <div className="text-2xl font-bold text-emerald-700">{stats.por_tipo?.dineraria || 0}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-hacer">
            <CardContent className="pt-4">
              <div className="text-xs text-amber-700 flex items-center gap-1">
                <Hammer className="w-3 h-3" /> De hacer
              </div>
              <div className="text-2xl font-bold text-amber-700">{stats.por_tipo?.hacer || 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Botón Nueva si embedded */}
      {embedded && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">Tramitación de ejecuciones judiciales</div>
          <Button onClick={onNueva} className="bg-violet-700 hover:bg-violet-800" data-testid="btn-nueva-ejecucion-tab">
            <Plus className="w-4 h-4 mr-2" /> Nueva ejecución
          </Button>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <Input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onBuscar()}
                placeholder="Buscar por nº registro, objeto, notas..."
                className="pl-9"
                data-testid="input-busqueda-ejecuciones"
              />
            </div>
            <Select value={filtroEstado} onValueChange={(v) => { setFiltroEstado(v); setPage(1); }}>
              <SelectTrigger data-testid="filtro-estado-ejecuciones"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los estados</SelectItem>
                {Object.entries(ESTADOS_LABEL).map(([k, v]) =>
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <Select value={filtroTipo} onValueChange={(v) => { setFiltroTipo(v); setPage(1); }}>
              <SelectTrigger data-testid="filtro-tipo-ejecuciones"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los tipos</SelectItem>
                <SelectItem value="dineraria">Dineraria</SelectItem>
                <SelectItem value="hacer">De hacer</SelectItem>
                <SelectItem value="mixta">Mixta</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-violet-700" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Gavel className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No hay ejecuciones que coincidan con los filtros</p>
              <Button variant="outline" className="mt-4" onClick={onNueva} data-testid="btn-nueva-ejecucion-empty">
                <Plus className="w-4 h-4 mr-2" /> Crear la primera
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº Registro</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Expediente</TableHead>{/* __V67B_HEAD__ */}
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Importe total</TableHead>
                  <TableHead>Fecha resolución</TableHead>
                  <TableHead>Objeto</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(it => (
                  <TableRow key={it.id} className="cursor-pointer hover:bg-violet-50" onClick={() => navigate(`/ejecuciones/${it.id}`)} data-testid={`row-ejecucion-${it.id}`}>
                    <TableCell className="font-mono text-sm font-semibold text-violet-800">{it.numero_registro}</TableCell>
                    <TableCell>{renderTipo(it.tipo)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {it.expediente_numero ? (
                        <a href={`/expedientes/${it.expediente_id}`} target="_blank" rel="noreferrer"
                           className="text-blue-700 hover:underline font-mono text-xs" data-testid={`link-exp-${it.expediente_id}`}>
                          {it.expediente_numero}
                        </a>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </TableCell>{/* __V67_CELL__ */}
                    <TableCell>{renderEstado(it.estado)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {it.tipo === 'hacer'
                        ? <span className="text-gray-400 italic">—</span>
                        : (it.importe_total || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {it.fecha_resolucion_ejecutar || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-gray-700 max-w-md truncate">
                      {it.objeto_descripcion || <span className="text-gray-400 italic">sin descripción</span>}
                    </TableCell>
                    <TableCell>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Paginación */}
      {total > pageSize && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">
            Mostrando {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} de {total}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Anterior</Button>
            <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage(page + 1)}>Siguiente</Button>
          </div>
        </div>
      )}

      {/* Modal alta */}
      {formOpen && (
        <EjecucionForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onCreated={onCreated}
        />
      )}
    </div>
  );
};

export default Ejecuciones;
